// src/vatandas/pages/VatandasLoginPage.js
import React, { useMemo, useState, useEffect, useCallback } from "react";
import "./LoginPage.css";

import {
  BuildingOffice2Icon,
  UserIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowRightOnRectangleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  XMarkIcon, //   toast kapat
} from "@heroicons/react/24/outline";

import api from "../services/vatandasApi";
import { safeNextPath } from "../utils/safeNext";

const LS_ACCESS = "accessToken";
const LS_REFRESH = "refreshToken";
const LS_USER = "user";
const LS_ACTIVE_UNIT_SLUG = "activeUnitSlug";

//   NEW (bozmadan): belediye tarafında da kullanılan key’leri temizlemek için
const LS_ACTIVE_UNIT_ID = "activeUnitId";
const LS_ACTIVE_EFFECTIVE_ROLE = "activeEffectiveRole";

function Toast({ toast, onClose }) {
  if (!toast) return null;

  const Icon = toast.type === "error" ? ExclamationTriangleIcon : ShieldCheckIcon;

  return (
    <div className={`login-toast ${toast.type}`} role="status" aria-live="polite">
      <Icon className="login-toast-ic" aria-hidden="true" />
      <div className="login-toast-body">
        <div className="login-toast-title">{toast.title}</div>
        <div className="login-toast-text">{toast.message}</div>
      </div>
      <button className="login-toast-close" onClick={onClose} aria-label="Kapat">
        <XMarkIcon className="login-toast-close-ic" aria-hidden="true" />
      </button>
    </div>
  );
}

const VatandasLoginPage = () => {
  const [credentials, setCredentials] = useState({ username: "", password: "" });

  const [error, setError] = useState("");
  const [toast, setToast] = useState(null); //   toast state
  const [isWaitingUnit, setIsWaitingUnit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isDisabled = isSubmitting || isWaitingUnit;
  const title = useMemo(() => "Arsuz Belediyesi Yönetim Paneli", []);

  // FAZ-63 — DERİN LİNK HEDEFİ.
  // SMS'teki kısa link (veya herhangi bir derin link) girişsiz açılınca buraya
  // "?next=/yonetim/gorevler/12" ile geliyoruz; giriş başarılı olunca kullanıcı
  // rolüne göre sabit sayfaya değil, GELMEK İSTEDİĞİ sayfaya gider.
  // Süzgeç için bkz. utils/safeNext.js (açık yönlendirme koruması).
  // next YOKSA aşağıdaki mevcut landing mantığı harfiyen aynı çalışır.
  const nextPath = useMemo(() => {
    try {
      return safeNextPath(new URLSearchParams(window.location.search).get("next"));
    } catch {
      return "";
    }
  }, []);

  const showToast = useCallback((type, title, message) => {
    setToast({ type, title, message });
  }, []);

  //   Toast auto close
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(t);
  }, [toast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((p) => ({ ...p, [name]: value }));
  };

  const clearLegacySession = () => {
    localStorage.removeItem(LS_ACCESS);
    localStorage.removeItem(LS_REFRESH);
    localStorage.removeItem(LS_USER);
    localStorage.removeItem(LS_ACTIVE_UNIT_SLUG);

    //   NEW (bozmadan): halk masası / multi-unit akışlarında stale kalmasın
    localStorage.removeItem(LS_ACTIVE_UNIT_ID);
    localStorage.removeItem(LS_ACTIVE_EFFECTIVE_ROLE);
  };

  const persistSession = ({ access, refresh, user }) => {
    localStorage.setItem(LS_ACCESS, access);
    localStorage.setItem(LS_REFRESH, refresh);
    localStorage.setItem(LS_USER, JSON.stringify(user));
  };

  const goLegacy = ({ path, access, user }) => {
    const userParam = encodeURIComponent(JSON.stringify(user));
    // FAZ-63: hedef zaten sorgu dizisi taşıyabilir (derin link
    // "/yonetim/gorevlerim?kume=acik"). Ayraç sabit "?" olursa ikinci bir soru
    // işareti eklenir ve adres bozulur — ayracı yola göre seç.
    const sep = String(path || "").includes("?") ? "&" : "?";
    window.location.href = `${path}${sep}token=${access}&user=${userParam}`;
  };

  const getUnitSlugWithFallback = async ({ unitId, access, userMe }) => {
    const units = Array.isArray(userMe?.units) ? userMe.units : [];
    const found = units.find((u) => Number(u.unit_id) === Number(unitId) && u.unit_slug);
    if (found?.unit_slug) return found.unit_slug;

    const unitDetail = await api.get(`/common/units/${unitId}`, {
      headers: { Authorization: `Bearer ${access}` },
    });
    return unitDetail?.data?.slug;
  };

  const normalizeActiveUnits = (userMe) => {
    const units = Array.isArray(userMe?.units) ? userMe.units : [];
    return units.filter((u) => u?.is_active !== false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setToast(null);
    setIsSubmitting(true);

    try {
      clearLegacySession();

      const tokenRes = await api.post("/token/", credentials);
      const { access, refresh } = tokenRes.data || {};
      if (!access || !refresh) throw new Error("Token alınamadı.");

      const meRes = await api.get("/common/users/me/", {
        headers: { Authorization: `Bearer ${access}` },
      });
      const userMe = meRes.data;

      if (!userMe?.id || !userMe?.username) throw new Error("Kullanıcı bilgisi alınamadı.");

      persistSession({ access, refresh, user: userMe });

      // FAZ-63 — DERİN LİNK: hedef varsa landing dallarının TAMAMINI atla.
      // Bilinçli olarak rol/üyelik dallarından ÖNCE duruyor: kullanıcı zaten
      // nereye gitmek istediğini söyledi. Hedef sayfanın kendi izin kapısı
      // (PermissionGate/ModuleGate) yetkisizse zaten devreye girer.
      if (nextPath) {
        goLegacy({ path: nextPath, access, user: userMe });
        return;
      }

      // FAZ-16 — permission_ui AÇIKSA landing kararını TAMAMEN BelediyeApp'e
      // (resolveLanding) devret. Aksi halde aşağıdaki legacy rol/üyelik dalları YENİ
      // rolleri (role_ref, role="") yanlış yönlendirir: role_ref halk_masasi 1 üyelikle
      // → birimli dashboard (halk-masası DEĞİL); role_ref başkan/müdür → yanlış dal;
      // 2 üyelikli → birim-sec. resolveLanding izin + ui_profile + scope'tan doğru iner.
      // uiOn=false → aşağıdaki legacy davranış AYNEN korunur.
      const uiOn =
        userMe?.enabled_modules?.permission_ui === true &&
        Array.isArray(userMe?.permissions) &&
        userMe.permissions.length > 0;
      if (uiOn) {
        goLegacy({ path: "/yonetim", access, user: userMe });
        return;
      }

      //   Başkan
      if (userMe.role === "başkan") {
        goLegacy({ path: `/yonetim/baskanlik/dashboard`, access, user: userMe });
        return;
      }

      //   NEW: Halk Masası (personel_2) — birim seçimi yok, direkt rota
      if (userMe.role === "personel_2") {
        goLegacy({ path: `/yonetim/halk-masasi`, access, user: userMe });
        return;
      }

      const activeUnits = normalizeActiveUnits(userMe);

      // Multi-unit: seçime gönder
      if (activeUnits.length >= 2) {
        goLegacy({ path: `/yonetim/birim-sec`, access, user: userMe });
        return;
      }

      // Single membership unit
      if (activeUnits.length === 1 && activeUnits[0]?.unit_slug) {
        const slug = activeUnits[0].unit_slug;

        if (userMe.role === "saha_personeli") {
          goLegacy({ path: `/yonetim/${slug}/saha`, access, user: userMe });
          return;
        }
        goLegacy({ path: `/yonetim/${slug}/dashboard`, access, user: userMe });
        return;
      }

      // Legacy fallback: user.unit üzerinden slug çöz
      const unitId = userMe.unit;
      if (!unitId || typeof unitId !== "number") {
        // uiOn olsaydı en başta /yonetim'e devredilmişti (resolveLanding) → buraya
        // yalnız uiOn=false (legacy) + birimsiz kullanıcı düşer.
        const msg = "Bu kullanıcıya ait birim bilgisi bulunamadı.";
        setError(msg);
        showToast("error", "Giriş", msg);
        return;
      }

      setIsWaitingUnit(true);

      const slug = await getUnitSlugWithFallback({ unitId, access, userMe });
      if (!slug) {
        const msg = "Birim bilgisi alınamadı. Lütfen tekrar deneyin veya yöneticinize başvurun.";
        setError(msg);
        showToast("error", "Giriş", msg);
        return;
      }

      if (userMe.role === "saha_personeli") {
        goLegacy({ path: `/yonetim/${slug}/saha`, access, user: userMe });
        return;
      }

      goLegacy({ path: `/yonetim/${slug}/dashboard`, access, user: userMe });
    } catch (err) {
      console.error("Login error:", err);

      const status = err?.response?.status;

      //   Yanlış giriş: toast + inline alert
      if (status === 401 || status === 400) {
        const msg = "Kullanıcı adı veya parola hatalı.";
        setError(msg);
        showToast("error", "Giriş Başarısız", msg);
      } else {
        const msg = "Giriş sırasında bir hata oluştu. Lütfen tekrar deneyin.";
        setError(msg);
        showToast("error", "Hata", msg);
      }

      setIsWaitingUnit(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-shell">
        <div className="login-card v-card">
          <div className="login-head">
            <div className="login-badge" aria-hidden="true">
              <BuildingOffice2Icon className="login-badge-icon" />
            </div>

            <div className="login-title-wrap">
              <h1 className="login-title">{title}</h1>

              <div className="login-trust" aria-label="Güvenli giriş bilgisi">
                <ShieldCheckIcon className="login-trust-icon" aria-hidden="true" />
                <span className="login-trust-text">
                  Giriş bilgileriniz güvenli şekilde işlenir ve korunur.
                </span>
              </div>
            </div>
          </div>

          {/*   inline alert */}
          {error ? (
            <div className="login-alert" role="alert">
              <ExclamationTriangleIcon className="login-alert-icon" />
              <div className="login-alert-text">{error}</div>
            </div>
          ) : null}

          {isWaitingUnit ? (
            <div className="login-wait">
              <div className="login-wait-row">
                <ArrowPathIcon className="login-wait-icon" />
                <div className="login-wait-text">Birim bilgisi alınıyor, lütfen bekleyin...</div>
              </div>
            </div>
          ) : (
            <form className="login-form v-stack" onSubmit={handleSubmit}>
              <div className="login-field">
                <label className="login-label" htmlFor="username">
                  Kullanıcı Adı
                </label>

                <div className="login-input-wrap">
                  <UserIcon className="login-input-icon" aria-hidden="true" />
                  <input
                    className="v-input login-input"
                    type="text"
                    id="username"
                    name="username"
                    placeholder="Kullanıcı Adı"
                    autoComplete="username"
                    required
                    value={credentials.username}
                    onChange={handleChange}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="login-field">
                <label className="login-label" htmlFor="password">
                  Parola
                </label>

                <div className="login-input-wrap">
                  <LockClosedIcon className="login-input-icon" aria-hidden="true" />
                  <input
                    className="v-input login-input"
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    placeholder="Parola"
                    autoComplete="current-password"
                    required
                    value={credentials.password}
                    onChange={handleChange}
                    disabled={isDisabled}
                  />

                  <button
                    type="button"
                    className="login-eye"
                    onClick={() => setShowPassword((p) => !p)}
                    aria-label={showPassword ? "Parolayı gizle" : "Parolayı göster"}
                    disabled={isDisabled}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="login-eye-icon" />
                    ) : (
                      <EyeIcon className="login-eye-icon" />
                    )}
                  </button>
                </div>
              </div>

              <button className="v-btn v-btn--primary login-submit" type="submit" disabled={isDisabled}>
                <span className="login-submit-text">Giriş Yap</span>
                {isSubmitting ? (
                  <ArrowPathIcon className="login-submit-spin" />
                ) : (
                  <ArrowRightOnRectangleIcon className="login-submit-icon" />
                )}
              </button>

              <div className="login-footnote">
                <span className="v-muted">Sorun yaşıyorsanız birim yöneticinizle iletişime geçin.</span>
              </div>
            </form>
          )}
        </div>

        {/*   Toast (sayfanın sağ üstü) */}
        <Toast toast={toast} onClose={() => setToast(null)} />
      </div>
    </div>
  );
};

export default VatandasLoginPage;
